import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import {
  ADMIN_EMAIL,
  auth,
  formatUser,
  getRedirectResultUser,
  loginWithFirebase,
  loginWithGoogle as loginWithGoogleFirebase,
  logoutWithFirebase,
  signupWithFirebase,
} from './firebaseAuth';

const AuthContext = createContext(null);
const IDLE_TIMEOUT_MS = 15 * 60 * 1000;
const IDLE_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
const AUTH_FLAG_KEY = 'sd_auth';
const AUTH_USER_KEY = 'sd_auth_user';

function normalizeUserRole(user) {
  if (!user || typeof user !== 'object') return null;
  const email = String(user.email || '').trim().toLowerCase();
  const isAdmin = email === ADMIN_EMAIL;
  return {
    ...user,
    email,
    role: isAdmin ? 'admin' : 'user',
  };
}

function readStoredUser() {
  if (typeof window === 'undefined') return null;

  try {
    const raw = localStorage.getItem(AUTH_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;

    const normalized = normalizeUserRole(parsed);
    if (parsed.role !== normalized.role) {
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(normalized));
    }
    return normalized;
  } catch (_) {
    return null;
  }
}

function persistStoredUser(user) {
  if (typeof window === 'undefined') return;
  const normalized = normalizeUserRole(user);
  localStorage.setItem(AUTH_FLAG_KEY, 'true');
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(normalized));
}

function clearStoredUser() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(AUTH_FLAG_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readStoredUser());
  const [loading, setLoading] = useState(() => !readStoredUser());

  useEffect(() => {
    let mounted = true;

    // Check for Google redirect result (on mobile web or redirected flows)
    getRedirectResultUser()
      .then((redirectUser) => {
        if (mounted && redirectUser) {
          setUser(redirectUser);
          persistStoredUser(redirectUser);
          setLoading(false);
        }
      })
      .catch(() => {});

    // Listen for Firebase native auth state changes
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (!mounted) return;
      if (firebaseUser) {
        const formatted = formatUser(firebaseUser);
        setUser(formatted);
        persistStoredUser(formatted);
      } else {
        const stored = readStoredUser();
        if (!stored) {
          setUser(null);
          clearStoredUser();
        }
      }
      setLoading(false);
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  const refreshSession = useCallback(async () => {
    if (auth.currentUser) {
      const u = formatUser(auth.currentUser);
      setUser(u);
      persistStoredUser(u);
      return u;
    }
    const stored = readStoredUser();
    setUser(stored);
    return stored;
  }, []);

  const login = useCallback(async ({ email, password }) => {
    const result = await loginWithFirebase({ email, password });
    if (result?.user) {
      setUser(result.user);
      persistStoredUser(result.user);
    }
    return result;
  }, []);

  const loginGoogle = useCallback(async () => {
    const result = await loginWithGoogleFirebase();
    if (result?.user) {
      setUser(result.user);
      persistStoredUser(result.user);
    }
    return result;
  }, []);

  const signup = useCallback(async ({ name, email, password }) => {
    const result = await signupWithFirebase({ name, email, password });
    if (result?.user) {
      setUser(result.user);
      persistStoredUser(result.user);
    }
    return result;
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutWithFirebase();
    } finally {
      setUser(null);
      clearStoredUser();
    }
  }, []);

  useEffect(() => {
    if (!user) return undefined;

    let timeoutId = window.setTimeout(() => {
      logout().catch(() => {});
    }, IDLE_TIMEOUT_MS);

    const resetTimer = () => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        logout().catch(() => {});
      }, IDLE_TIMEOUT_MS);
    };

    IDLE_EVENTS.forEach((eventName) => window.addEventListener(eventName, resetTimer, { passive: true }));

    return () => {
      window.clearTimeout(timeoutId);
      IDLE_EVENTS.forEach((eventName) => window.removeEventListener(eventName, resetTimer));
    };
  }, [user, logout]);

  const value = useMemo(() => {
    const email = String(user?.email || '').trim().toLowerCase();
    const isAdmin = Boolean(user) && email === ADMIN_EMAIL && user?.role === 'admin';

    return {
      user,
      loading,
      isAuthenticated: Boolean(user),
      isAdmin,
      refreshSession,
      login,
      loginGoogle,
      signup,
      logout,
      setUser,
    };
  }, [user, loading, refreshSession, login, loginGoogle, signup, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return ctx;
}
